import type { TourStep } from '../components/onboarding/GuidedTour';

/**
 * Build the guided-tour step list. Track-aware so middle-school students
 * get a tour about Quests/Workshops and Bagrut students get a tour about
 * exam practice / learning path.
 *
 * Each step refers to a `data-tour="..."` attribute on a real element.
 * The tour renders centered if the target isn't found, so we can include
 * optional steps that gracefully degrade.
 */
// Per-grade copy for the tour. Lets the tooltip say
// "10 שאלות לכיתה ז׳ — בסיסיות במספרים, שברים, אחוזים" instead of a
// generic "questions for your level".
const GRADE_DESC: Record<string, { exam: string; practice: string; lessons: string; tagline: string }> = {
  el1: {
    exam: 'מבחן כיתה א׳ — 4 שאלות, 10 דקות. קצר ומותאם!',
    practice: 'שאלות לכיתה א׳ — תחושת מספר, חיבור וחיסור עד 20. עם רמזים ופתרון מלא.',
    lessons: 'הספריה לכיתה א׳ — תחושת מספר, ערך מקום, חיבור וחיסור, מדידות וגאומטריה ראשונית.',
    tagline: 'הסיור הזה תמיד זמין מהגדרות. בוא נראה איך כיף ללמוד!',
  },
  el2: {
    exam: 'מבחן כיתה ב׳ — 5 שאלות, 12 דקות.',
    practice: 'שאלות לכיתה ב׳ — ערך מקום, חיבור עד 100, התחלת כפל.',
    lessons: 'הספריה לכיתה ב׳ — ערך מקום, חיבור וחיסור מורכבים יותר, כפל בסיסי, מדידות.',
    tagline: 'הסיור הזה תמיד זמין מהגדרות. בוא נראה איך כיף ללמוד!',
  },
  el3: {
    exam: 'מבחן כיתה ג׳ — 5 שאלות, 15 דקות.',
    practice: 'שאלות לכיתה ג׳ — כפל, חילוק, התחלת שברים, מדידות.',
    lessons: 'הספריה לכיתה ג׳ — כפל, חילוק, שברים בסיסיים, גאומטריה, נתונים פשוטים.',
    tagline: 'הסיור הזה תמיד זמין מהגדרות. בוא נראה איך כיף ללמוד!',
  },
  el4: {
    exam: 'מבחן כיתה ד׳ — 5 שאלות, 18 דקות.',
    practice: 'שאלות לכיתה ד׳ — שברים מורכבים, היקף, שטח, התחלת אלגברה (קופסה ריקה).',
    lessons: 'הספריה לכיתה ד׳ — שברים, שטח/היקף, מדידות, התחלת אלגברה (משוואות עם קופסה ריקה).',
    tagline: 'הסיור הזה תמיד זמין מהגדרות. בוא נראה איך כיף ללמוד!',
  },
  el5: {
    exam: 'מבחן כיתה ה׳ — 5 שאלות, 20 דקות.',
    practice: 'שאלות לכיתה ה׳ — שברים עשרוניים, אחוזים, יחס, נפח.',
    lessons: 'הספריה לכיתה ה׳ — עשרוניים, אחוזים, יחס, מערכת צירים בסיסית, הסתברות.',
    tagline: 'הסיור הזה תמיד זמין מהגדרות. בוא נראה איך כיף ללמוד!',
  },
  el6: {
    exam: 'מבחן כיתה ו׳ — 6 שאלות, 22 דקות. הכי קרוב לחטיבה!',
    practice: 'שאלות לכיתה ו׳ — אחוזים מורכבים, יחס, מערכת צירים, אלגברה מוקדמת. הכנה לחטיבה.',
    lessons: 'הספריה לכיתה ו׳ — סקירת כל מה שלמדת + אלגברה מוקדמת + מערכת צירים. הכנה לחט״ב.',
    tagline: 'הסיור הזה תמיד זמין מהגדרות. בקרוב — חטיבה!',
  },
  ms7: {
    exam: 'מבחן כיתה ז׳ — 8 שאלות, 30 דקות. טיימר חי וסימון שאלות לבדיקה.',
    practice: '10 שאלות לכיתה ז׳ — מספרים מכוונים, שברים, אחוזים, יחס ומשוואות בסיסיות.',
    lessons: 'הספריה לכיתה ז׳ כוללת מספרים מכוונים, שברים, אחוזים, יחס, ביטויים אלגבריים ומשוואות.',
    tagline: 'הסיור הזה תמיד זמין מהגדרות. אפשר לפתוח שיעור או מסע חקר עכשיו.',
  },
  ms8: {
    exam: 'מבחן כיתה ח׳ — 10 שאלות, 35 דקות.',
    practice: '10 שאלות לכיתה ח׳ — פונקציה קווית, מערכות, פיתגורס ודמיון משולשים.',
    lessons: 'הספריה לכיתה ח׳ כוללת פונקציה קווית, מערכות משוואות, דמיון משולשים ופיתגורס.',
    tagline: 'הסיור הזה תמיד זמין מהגדרות. אם תרצה/י את המדריך האנימטיבי המלא — גם הוא שם.',
  },
  ms9: {
    exam: 'מבחן כיתה ט׳ — 12 שאלות, 45 דקות. סימולציה רצינית יותר לקראת תיכון.',
    practice: '10 שאלות לכיתה ט׳ — פונקציה ריבועית, פרבולה, סטטיסטיקה והסתברות.',
    lessons: 'הספריה לכיתה ט׳ כוללת פרבולה, סטטיסטיקה מתקדמת והסתברות — הכנה לתיכון.',
    tagline: 'הסיור הזה תמיד זמין מהגדרות. אם תרצה/י את המדריך האנימטיבי המלא — גם הוא שם.',
  },
  '3u': {
    exam: 'סימולציה ל-3 יח״ל — 20 שאלות, 90 דקות. טיימר חי, מד קצב וסימון שאלות.',
    practice: 'שאלות במסלול 3 יח״ל — מעודכנות לפי החולשות שלך מהמערכת.',
    lessons: 'הסבר מהשורש לכל נושאי 3 יח״ל — אלגברה, גאומטריה, פונקציות בסיסיות.',
    tagline: 'הסיור תמיד זמין מהגדרות. אפשר לקפוץ ישר לתרגול.',
  },
  '4u': {
    exam: 'סימולציה ל-4 יח״ל — 25 שאלות, 120 דקות.',
    practice: 'שאלות במסלול 4 יח״ל — כולל נגזרות, סדרות וטריגונומטריה.',
    lessons: 'הסברים מקיפים לכל נושאי 4 יח״ל, כולל בעיות מילוליות מורכבות.',
    tagline: 'הסיור תמיד זמין מהגדרות. אפשר לקפוץ ישר לתרגול.',
  },
  '5u': {
    exam: 'סימולציה ל-5 יח״ל — 30 שאלות, 150 דקות. הכי קרוב לבגרות אמיתית.',
    practice: 'שאלות במסלול 5 יח״ל — חדו״א מתקדם, וקטורים, מספרים מרוכבים.',
    lessons: 'ספרייה מלאה ל-5 יח״ל — חדו״א, אנליטיקה, וקטורים, פונקציות מתקדמות.',
    tagline: 'הסיור תמיד זמין מהגדרות. אפשר לקפוץ ישר לתרגול.',
  },
};

export function buildTourSteps(track: string | undefined): TourStep[] {
  const trackStr = String(track || '');
  const isMS = trackStr.startsWith('ms');
  const isEL = trackStr.startsWith('el');
  const showQuestBoard = isMS || isEL;  // quest board surfaces for both
  const copy = GRADE_DESC[track ?? ''] ?? GRADE_DESC['5u'];
  const steps: TourStep[] = [];

  // 1. Open with the welcome card so the student recognizes their own avatar
  steps.push({
    selector: 'welcome-card',
    title: 'זה הבית שלך 🏠',
    description: 'הדשבורד שלך — אווטאר, רמה, רצף, ומה לעשות עכשיו. כל הפעילות שלך מתעדכנת כאן.',
    placement: 'bottom',
  });

  if (showQuestBoard) {
    // 2. Kids-only (el + ms): highlight the Quest Board CTA
    steps.push({
      selector: 'quest-board',
      title: 'מסעות חקר 🗺️',
      description: isEL
        ? 'מסעות עם סיפור: חנות הכיתה, מסעדת השברים, מפת אוצר ועוד. תתחיל כאן!'
        : 'מסעות מהמציאות → סדנה אינטראקטיבית → XP. תתחיל כאן אם לא יודע ממה להתחיל.',
      placement: 'bottom',
    });
  }

  // 3. Practice nav — works on both desktop pill row and mobile bottom nav
  steps.push({
    selector: 'nav-practice',
    title: 'תרגול ✏️',
    description: copy.practice,
    placement: 'bottom',
  });

  // 4. Lessons — framed differently for MS (refresher) vs Bagrut (curriculum)
  steps.push({
    selector: 'nav-lessons',
    title: 'ספריית הנושאים 📚',
    description: copy.lessons,
    placement: 'bottom',
  });

  // 5. Achievements/stats hub
  steps.push({
    selector: 'nav-achievements',
    title: 'הסטטיסטיקה שלך 📊',
    description: 'heatmap של פעילות, רמת שליטה לכל נושא, וכרטיס שיתוף יפה לאינסטגרם. גם דוח להורים להדפסה.',
    placement: 'bottom',
  });

  // 6. Exam — copy per-grade ('20 שאלות, 90 דקות' for 3u, '8 שאלות, 30 דקות' for ms7).
  steps.push({
    selector: 'nav-exam',
    title: 'בחינת זמן 📝',
    description: copy.exam,
    placement: 'bottom',
  });

  // 7. Settings — universal final destination
  steps.push({
    selector: 'nav-settings',
    title: 'הגדרות ⚙️',
    description: 'נושאים, אווטאר, צלילים, שינוי מסלול, הצגת מדריך מלא חזרה. הכל פה.',
    placement: 'bottom',
  });

  // 8. Closing scene — no target, render centered
  steps.push({
    selector: null,
    title: (isMS || isEL) ? 'מוכן/ה להתחיל? 🚀' : 'בהצלחה! 🎓',
    description: copy.tagline,
  });

  return steps;
}
